import { buildLicensedPortalNav } from "../portal/licensedNav";
import { isWorkspacePath } from "../layout/workspaceRoutes";
import { useLocation } from "react-router-dom";
import {
  MacHeader,
  MacHeaderAction,
} from "src/shared/ui/src/design/MacControls";
import {
  InterfaceDesignHeaderSlot,
  useInterfaceDesign,
} from "src/shared/ui/src/design/InterfaceDesign";
import { Menu, ListItemIcon, ListItemText } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AppsIcon from "@mui/icons-material/Apps";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatedIconButton } from "src/shared/ui/src/buttons/AnimatedIconButton";
import dico from "src/shared/util/src/language/dico";
import { ROLE_NAME, userHasRole } from "src/shared/util/src/roles/role-access";
import { UserModel } from "src/shared/util/src/types";
import { useUserStore } from "src/stores/userStore";
import Logo from "../misc/Logo";
import appIcon from "../../assets/icon.png";

interface MenuItem {
  text: string;
  onClick?: () => void;
  items?: SubMenuItem[];
  icon?: React.ReactNode;
}

interface SubMenuItem {
  text: string;
  onClick: () => void;
}

interface MobileNavigationProps {
  handleDrawerClose: () => void;
  mobileMenuItems: MenuItem[];
  authMenuItems: MenuItem[];
  isDrawerOpen: boolean;
  toggleDrawer: () => void;
}

// Desktop navigation menu items
const DesktopNavigation = () => {
  return (
    <Box
      sx={{ flexGrow: 1, display: "flex", gap: 2, justifyContent: "center" }}
    >
      <AnimatedIconButton icon={<HomeIcon />} to="/">
        {dico().home}
      </AnimatedIconButton>

      {/* <AnimatedIconButton
        icon={<PriceChangeIcon />}
        to="/pricing"
      >
        {dico().webHome.pricing.title}
      </AnimatedIconButton> */}

      <AnimatedIconButton icon={<AppsIcon />} to="/app" external>
        {dico().webClient}
      </AnimatedIconButton>

      <AnimatedIconButton
        icon={<EmailIcon />}
        onClick={() => {
          window.location.href = "mailto:contact@9of9solutions.com";
        }}
      >
        {dico().webHome.contact.title}
      </AnimatedIconButton>
    </Box>
  );
};

// Desktop action buttons
const DesktopActionButtons = ({
  currentUser,
  navigate,
  signOut,
}: {
  currentUser: UserModel | null;
  navigate: (path: string) => void;
  signOut: () => void;
}) => {
  const strings = dico();
  const hasAdmin = userHasRole(currentUser, ROLE_NAME.Admin);
  const hasLicensed = userHasRole(currentUser, ROLE_NAME.Licensed);
  const hasDisplayEditor = userHasRole(currentUser, ROLE_NAME.DisplayEditor);
  const hasSpeaker = userHasRole(currentUser, ROLE_NAME.Speaker);
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        justifyContent: "right",
      }}
    >
      {currentUser ? (
        <>
          <Box sx={{ display: "flex", gap: 1 }}>
            {hasAdmin && (
              <AnimatedIconButton
                icon={<AdminPanelSettingsIcon />}
                onClick={() => navigate("/admin")}
                sx={{ color: "primary.main" }}
              >
                {strings.navigation.administration}
              </AnimatedIconButton>
            )}
            {hasLicensed && (
              <AnimatedIconButton
                icon={<CloudDownloadIcon />}
                onClick={() => navigate("/licensed")}
                sx={{ color: "primary.main" }}
              >
                {strings.navigation.licensedPortal}
              </AnimatedIconButton>
            )}
            {hasDisplayEditor && !hasLicensed && (
              <AnimatedIconButton
                icon={<AppsIcon />}
                onClick={() => navigate("/licensed/layouts")}
                sx={{ color: "primary.main" }}
              >
                {strings.navigation.displayEditorPortal}
              </AnimatedIconButton>
            )}
            {hasSpeaker && (
              <AnimatedIconButton
                icon={<AppsIcon />}
                onClick={() => navigate("/speaker")}
                sx={{ color: "primary.main" }}
              >
                {strings.navigation.speakerPortal}
              </AnimatedIconButton>
            )}
          </Box>
          <AnimatedIconButton
            icon={<LogoutIcon />}
            onClick={() => {
              signOut();
              navigate("/sign-in");
            }}
            sx={{ color: "red" }}
          >
            {strings.navigation.logOut}
          </AnimatedIconButton>
        </>
      ) : (
        <motion.div
          style={{
            border: "1px solid transparent",
            borderRadius: "8px",
            padding: "0.5px",
            transition: "border-color 0.3s ease-in-out",
          }}
          whileHover={{ borderColor: "grey" }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            sx={{ color: "primary.main" }}
            onClick={() => navigate("/sign-in")}
            startIcon={<LoginIcon />}
          >
            {strings.navigation.logIn}
          </Button>
        </motion.div>
      )}
    </Box>
  );
};

// Mobile navigation
const MobileNavigation = ({
  handleDrawerClose,
  mobileMenuItems,
  authMenuItems,
  isDrawerOpen,
  toggleDrawer,
}: MobileNavigationProps) => {
  const handleButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // toggleDrawer();
    if (isDrawerOpen) {
      handleDrawerClose();
    } else {
      toggleDrawer();
    }
  };

  return (
    <>
      <motion.div
        style={{
          marginLeft: "auto",
          position: "relative",
          width: 48,
          height: 48,
          zIndex: 2000,
        }}
      >
        <motion.div
          animate={{ rotate: isDrawerOpen ? 90 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <IconButton
            onClick={(e) => handleButtonClick(e)}
            size="large"
            edge="end"
            color="primary"
            aria-label={isDrawerOpen ? "fermer le menu" : "ouvrir le menu"}
          >
            {window.location.pathname !== "/app" &&
              (isDrawerOpen ? <CloseIcon /> : <MenuIcon />)}
          </IconButton>
        </motion.div>
      </motion.div>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => handleDrawerClose()}
        hideBackdrop
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              top: "70px",
              height: "calc(100% - 70px)",
              boxShadow: "none",
              borderLeft: "none",
              zIndex: 1100, // En dessous du header (AppBar a un z-index de 1200 par défaut)
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "auto",
          }}
        >
          {/* Content */}
          <Box sx={{ flex: 1, p: 2 }}>
            {mobileMenuItems.map((item, index) => (
              <Box key={index}>
                {item.items ? (
                  <Accordion
                    elevation={0}
                    sx={{
                      "&:before": { display: "none" },
                      mb: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 1,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Typography>{item.text}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {item.items.map((subItem, subIndex) => (
                        <motion.div
                          key={subIndex}
                          whileHover={{ x: 5 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            fullWidth
                            onClick={subItem.onClick}
                            sx={{
                              justifyContent: "flex-start",
                              py: 1.5,
                              pl: 3,
                              color: "text.primary",
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            {subItem.text}
                          </Button>
                        </motion.div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      fullWidth
                      onClick={item.onClick}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.5,
                        mb: 1,
                        color: "text.primary",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                      startIcon={item.icon}
                    >
                      {item.text}
                    </Button>
                  </motion.div>
                )}
              </Box>
            ))}

            {/* Divider before auth items */}
            <Box sx={{ height: 1, bgcolor: "white", my: 2 }} />

            {/* Auth items */}
            {authMenuItems.map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  fullWidth
                  onClick={item.onClick}
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                    mb: 1,
                    color:
                      item.text === dico().navigation.logOut
                        ? "error.main"
                        : "text.primary",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                  startIcon={item.icon}
                >
                  {item.text}
                </Button>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

// Main MenuHeader component
const MenuHeader = () => {
  const { pathname, search } = useLocation();
  const { mode, colorMode } = useInterfaceDesign();
  const designWorkspace = isWorkspacePath(pathname);
  const [workspaceMenu, setWorkspaceMenu] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { currentUser, token, fetchUser, signOut } = useUserStore();

  useEffect(() => {
    if (!token || currentUser) return;
    fetchUser(token);
  }, [token, currentUser, fetchUser]);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleMenuClose = () => {
    setIsDrawerOpen(false);
  };

  // Mobile menu items
  const mobileMenuItems: MenuItem[] = [
    {
      text: dico().home,
      onClick: () => {
        navigate("/");
        handleMenuClose();
      },
      icon: <HomeIcon />,
    },

    // {
    //   text: dico().webHome.pricing.title,
    //   onClick: () => {
    //     navigate('/pricing');
    //     handleMenuClose(setMobileMenuAnchor);
    //   },
    //   icon: <PriceChangeIcon />
    // },
    {
      text: dico().meets,
      onClick: () => {
        window.open("/app", "_blank");
        handleMenuClose();
      },
      icon: <AppsIcon />,
    },
    {
      text: dico().webHome.contact.title,
      onClick: () => {
        window.location.href = "mailto:contact@9of9solutions.com";
        handleMenuClose();
      },
      icon: <EmailIcon />,
    },
  ];

  // Authentication menu items
  const strings = dico();
  const authMenuItems: MenuItem[] = currentUser
    ? [
        ...(userHasRole(currentUser, ROLE_NAME.Admin)
          ? [
              {
                text: strings.navigation.administration,
                onClick: () => {
                  navigate("/admin");
                  handleMenuClose();
                },
                icon: <AdminPanelSettingsIcon />,
              },
            ]
          : []),
        ...(userHasRole(currentUser, ROLE_NAME.Licensed)
          ? [
              {
                text: strings.navigation.licensedPortal,
                onClick: () => {
                  navigate("/licensed");
                  handleMenuClose();
                },
                icon: <CloudDownloadIcon />,
              },
            ]
          : []),
        ...(userHasRole(currentUser, ROLE_NAME.DisplayEditor) &&
        !userHasRole(currentUser, ROLE_NAME.Licensed)
          ? [
              {
                text: strings.navigation.displayEditorPortal,
                onClick: () => {
                  navigate("/licensed/layouts");
                  handleMenuClose();
                },
                icon: <AppsIcon />,
              },
            ]
          : []),
        ...(userHasRole(currentUser, ROLE_NAME.Speaker)
          ? [
              {
                text: strings.navigation.speakerPortal,
                onClick: () => {
                  navigate("/speaker");
                  handleMenuClose();
                },
                icon: <AppsIcon />,
              },
            ]
          : []),
        {
          text: strings.navigation.logOut,
          onClick: () => {
            signOut();
            navigate("/sign-in");
            handleMenuClose();
          },
          icon: <LogoutIcon />,
        },
      ]
    : [
        {
          text: strings.navigation.logIn,
          onClick: () => {
            navigate("/sign-in");
            handleMenuClose();
          },
          icon: <LoginIcon />,
        },
      ];

  const licensedMenuItems = buildLicensedPortalNav({
    planning: true,
    backup: true,
    layoutEditor: true,
    controlRoom: true,
    speaker: true,
  }).navGroups.flatMap((group) => group.items);
  const currentLicensedMenu = licensedMenuItems
    .filter(
      (item) => pathname === item.path || pathname.startsWith(`${item.path}/`),
    )
    .sort((a, b) => b.path.length - a.path.length)[0];
  const licensedPageTitle = /^\/licensed\/layout-editor(?:\/|$)/.test(pathname)
    ? strings.licensedPortal.layoutEditor.title
    : /^\/licensed\/planning(?:\/|$)/.test(pathname) ||
        (pathname === "/licensed/my-meets" &&
          new URLSearchParams(search).get("view") === "plannings")
      ? strings.licensedPortal.myMeets.tabPlannings
      : (currentLicensedMenu?.label ?? strings.navigation.licensedPortal);

  if (designWorkspace && mode === "modern")
    return (
      <MacHeader
        title="Meet Manager"
        name={
          pathname.startsWith("/admin")
            ? strings.navigation.administration
            : pathname === "/sign-in"
              ? strings.navigation.logIn
              : licensedPageTitle
        }
        leading={
          <MacHeaderAction
            aria-label={dico().home}
            onClick={() => navigate("/")}
          >
            <img
              src={appIcon}
              alt="Meet Manager"
              style={{
                width: 32,
                height: 32,
                objectFit: "contain",
                filter:
                  colorMode === "dark" ? "brightness(0) invert(1)" : "none",
              }}
            />
          </MacHeaderAction>
        }
        actions={
          <>
            <MacHeaderAction
              icon="grid"
              aria-haspopup="menu"
              aria-expanded={Boolean(workspaceMenu)}
              onClick={(event) => setWorkspaceMenu(event.currentTarget)}
            >
              Navigation
            </MacHeaderAction>
            <Menu
              anchorEl={workspaceMenu}
              open={Boolean(workspaceMenu)}
              onClose={() => setWorkspaceMenu(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 190,
                    mt: 0.5,
                    p: 0.5,
                    "& .MuiMenuItem-root": {
                      minHeight: 32,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    },
                    "& .MuiListItemIcon-root": { minWidth: 28 },
                    "& .MuiSvgIcon-root": { fontSize: 17 },
                    "& .MuiListItemText-primary": {
                      fontSize: 12,
                      color: "inherit",
                    },
                  },
                },
              }}
            >
              {[...mobileMenuItems, ...authMenuItems].map((item) => (
                <MenuItem
                  key={item.text}
                  sx={{
                    ...(item.text === strings.navigation.logOut
                      ? { borderTop: 1, borderColor: "divider", mt: 0.5 }
                      : {}),
                    color:
                      item.text === strings.navigation.logOut
                        ? "text.secondary"
                        : "text.primary",
                  }}
                  onClick={() => {
                    setWorkspaceMenu(null);
                    item.onClick?.();
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{item.text}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        }
      />
    );

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{ borderBottom: "1px solid #eaeaea" }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: "70px" }}>
          {/* Logo */}
          <Logo navigate={navigate} />

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <DesktopNavigation />
              <DesktopActionButtons
                currentUser={currentUser}
                navigate={navigate}
                signOut={signOut}
              />
            </>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <MobileNavigation
              handleDrawerClose={handleDrawerClose}
              mobileMenuItems={mobileMenuItems}
              authMenuItems={authMenuItems}
              isDrawerOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
            />
          )}
          {designWorkspace && <InterfaceDesignHeaderSlot />}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default MenuHeader;
